import { useAdobeTarget } from '@cinch-labs/adobe'
import { sendPostMessage } from '@cinch-labs/cinchapp-config'
import { Text } from '@cinch-labs/design-system'
import { AuthModal, storePreAuthFavourite } from '@cinch-labs/shared-auth-modal'
import { FavouriteVehicle, useFavouritesStore } from '@cinch-nx/data-favourites'
import { Actions, Context, VehicleListing } from '@cinch-nx/data-search'
import { UserStatus } from '@cinch-nx/data-user'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { datadogRum } from '@datadog/browser-rum'
import cx from 'classnames'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import { formatVehicleSpecs, isWindowUserAgentCinchApp } from '../../utils'
import {
  Card,
  ExperimentFavouriteCTA,
  FavouriteCTA,
  FinanceCalculatorCTA,
  Price,
  ProductImage,
  SpecsList,
  Subtitle,
  Title,
} from './components'
import { VehicleImageCarousel } from './components/vehicle-image-carousel/vehicle-image-carousel'
import {
  parseVehicleDoorTrim,
  productDetailUrl,
  sendFavouritesAnalyticEvent,
} from './utils'
import styles from './vehicle-card.module.css'
import { useResponsiveEnvironment } from '../../hooks'
import { useAnalytics } from '@cinch-nx/shared-analytics'
import { useSsrExperiments } from '@cinch-nx/adobe-server-side'
import { useSsrProps } from '../../context/ssr-props-context'

type ProductListCardRequiredVehicleListingKeys =
  | 'fuelType'
  | 'transmissionType'
  | 'mileage'
  | 'vehicleYear'
  | 'isReserved'
  | 'make'
  | 'model'
/**
 * VehicleListing, but with most properties made optional
 */
type ProductListCardVehicleListing = Partial<
  Omit<VehicleListing, ProductListCardRequiredVehicleListingKeys>
> &
  Pick<VehicleListing, ProductListCardRequiredVehicleListingKeys>

const CompareCTA = dynamic(
  () => import('./components/compare-cta/compare-cta'),
  {
    ssr: false,
  },
)

export interface ProductListCardProps extends ProductListCardVehicleListing {
  id: string
  image: string
  imageStyles?: string
  title: string
  subtitle: string
  isPending: boolean
  unavailable?: string
  isUnfavourited?: boolean
  hasBorder?: boolean
  isHoverable?: boolean
  redirectsToFPA?: boolean
  isSearchPage?: boolean
  customerHasActiveOrder?: boolean
  onClick?: () => Promise<void>
  onAdminFeesClick?: () => void
  favourites: FavouriteVehicle[]
  userStatus: UserStatus
  financeType?: string[]
}

const ERROR_MESSAGE = 'Image Unavailable'
const penceToPounds = (n: number): string => `${Math.round(n / 100).toFixed(0)}`

const VehicleCard = ({
  id,
  image,
  imageStyles,
  title,
  subtitle,
  isPending = false,
  isUnfavourited,
  unavailable,
  hasBorder = true,
  isHoverable = true,
  isSearchPage = false,
  redirectsToFPA = true,
  // customerHasActiveOrder = false,
  onClick,
  onAdminFeesClick,
  favourites = [],
  userStatus,
  financeType = [],
  ...vehicleData
}: ProductListCardProps) => {
  const {
    discountInPence,
    depositContributionInPence,
    make,
    model,
    modelYear,
    isAvailable,
    isReserved,
    fuelType,
    transmissionType,
    mileage,
    vehicleYear,
    priceIncludingAdminFee,
    quoteRegularPaymentInPence,
    quoteType,
    additionalImages,
    quoteTermMonths,
  } = vehicleData

  const shouldShowFinanceTypeFilter =
    readFromEnv(Env.SearchFinanceTypeFilter) === 'true'

  /**
   * isSaved is hardcoded and passed from above
   * onSave is not used within the parent
   * we have access to favourites at this level
   * we can set is saved here
   * onSave can also be handled at this level
   *
   * */
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false)
  const { addFavourite, removeFavourite } = useFavouritesStore()
  const { isDesktop } = useResponsiveEnvironment()
  const { trackEvent } = useAnalytics()
  const { getExperienceIdForExperiment } = useSsrExperiments()
  const { isSsrUserAgentCinchApp } = useSsrProps()
  const isCinchApp = isSsrUserAgentCinchApp || isWindowUserAgentCinchApp

  const isFavourited: FavouriteVehicle | undefined = favourites.find(
    (favourite: FavouriteVehicle) => favourite.vehicleId === id,
  )

  const shouldShowCarsOnOffer = Boolean(discountInPence)
  const shouldShowDepositContribution = Boolean(depositContributionInPence)

  const financeTypeValue = shouldShowFinanceTypeFilter
    ? financeType[0]
    : undefined

  const productDetailsLink = productDetailUrl({
    make,
    model,
    id,
    financeType: financeTypeValue,
  })
  const isSold = isAvailable === false

  // #region Multiple images
  const isSearchMultipleImagesFlagEnabled =
    readFromEnv(Env.SearchMultipleImages) === 'true'
  const isFinanceCalculatorCTAFlagEnabled =
    readFromEnv(Env.SearchFinanceCalculatorCTA) === 'true'

  // Multiple images experiment v2 (multivariate test)
  const searchMultipleImagesV2ExperienceId = useAdobeTarget(
    'SEARCH_MULTIPLE_IMAGES_V2_EXPERIMENT_ID',
    trackEvent,
  )?.ExperienceId
  const isSearchMultipleImagesV2Variant1 =
    searchMultipleImagesV2ExperienceId === '1'
  const isSearchMultipleImagesV2Variant2 =
    searchMultipleImagesV2ExperienceId === '2'

  const ssrExperimentName = readFromEnv(
    Env.SearchFinanceCalculatorCTAExperimentId,
  )
  const isSSRExperiment = getExperienceIdForExperiment(ssrExperimentName) === 1

  const shouldAllowFinanceCalculatorExperiment =
    isFinanceCalculatorCTAFlagEnabled && isSSRExperiment && !isCinchApp

  // are we in an experience that requires the carousel?
  const isSearchMultipleImagesExperimentActive =
    isSearchMultipleImagesV2Variant1 || isSearchMultipleImagesV2Variant2

  // only show the carousel if we're in an experiment, the feature flag is enabled,
  // and we actually have multiple images to display
  const shouldShowImageCarousel =
    isSearchMultipleImagesExperimentActive &&
    isSearchMultipleImagesFlagEnabled &&
    additionalImages &&
    additionalImages.length > 1
  // #endregion

  const tabTarget = isDesktop ? '_blank' : '_self'
  const relValue = isDesktop ? 'opener' : undefined

  const vehiclePrice = priceIncludingAdminFee

  const handleToggleFavourite = async () => {
    const messageToSend = {
      make,
      model,
      modelYear: Number(modelYear),
      vehicleId: id,
    }

    if (isFavourited) {
      await removeFavourite(id)

      if (!isWindowUserAgentCinchApp) return

      sendPostMessage('favouriteVehicleRemoved', messageToSend)

      datadogRum.addAction(Actions.CINCHAPP_POST_MESSAGE, {
        message: {
          type: 'favouriteVehicleRemoved',
          data: messageToSend,
        },
        service: Context.CINCHAPP,
        squad: Context.SQUAD,
      })
      return
    }

    await addFavourite(id)

    if (!isWindowUserAgentCinchApp) return

    sendPostMessage('favouriteVehicleAdded', messageToSend)

    datadogRum.addAction(Actions.CINCHAPP_POST_MESSAGE, {
      message: {
        type: 'favouriteVehicleAdded',
        data: messageToSend,
      },
      service: Context.CINCHAPP,
      squad: Context.SQUAD,
    })
  }

  const handleOnFavouriteClick = async () => {
    const pathnameWithSearchParams = `${window.location.pathname}${window.location.search}`
    const vehicleId = vehicleData?.vehicleId ?? ''
    storePreAuthFavourite({
      id: vehicleId,
      url: pathnameWithSearchParams,
    })
    sendFavouritesAnalyticEvent(userStatus === 'valid', Boolean(isFavourited), {
      vehicle: vehicleData,
    })
    if (userStatus === 'valid') {
      await handleToggleFavourite()
    } else {
      setIsAuthModalOpen(true)
    }
  }

  const handleAuthModalClose = () => {
    setIsAuthModalOpen(false)
  }

  const handleOnClick = () => {
    datadogRum.addAction(Actions.VEHICLE_CARD_CLICKED)

    onClick && onClick()
  }

  return (
    <Card
      data-testid="product-list-card"
      element="article"
      hasBorder={hasBorder}
      isHoverable={isHoverable}
      isSearchPage={isSearchPage}
    >
      <div className={styles['content']}>
        <div
          className={cx(styles['meta'], styles['hasAdminFees'], {
            [styles['collapsed']]: !hasBorder,
            [styles['pending']]: isPending && isSearchPage,
          })}
        >
          <div>
            <Title
              element="h3"
              className={styles['title']}
              fontSizeFixed="m"
              type="cardTitle"
              isPending={isPending}
              aria-describedby={
                isReserved ? `vehicle-reserved-${id}` : undefined
              }
            >
              {redirectsToFPA ? (
                <a
                  data-testid="product-list-card-link"
                  href={productDetailsLink}
                  className={styles['link']}
                  onClick={handleOnClick}
                  target={tabTarget}
                  rel={relValue}
                >
                  {title}
                  <noscript> {subtitle}</noscript>
                </a>
              ) : (
                <Text data-testid="product-list-card-text">
                  {title}
                  <noscript> {subtitle}</noscript>
                </Text>
              )}
            </Title>

            <Subtitle
              isPending={isPending}
              fontSize="s"
              className={styles['subtitle']}
            >
              {parseVehicleDoorTrim(subtitle)}
            </Subtitle>

            <SpecsList
              isPending={isPending}
              listStyleClassName={styles['specsList']}
              listItemClassName={styles['specsListItem']}
              items={formatVehicleSpecs({
                fuelType,
                transmissionType,
                mileage,
                modelYear: Number(vehicleYear || modelYear),
              })}
            />
          </div>

          {!!vehiclePrice && (
            <Price
              isPending={isPending}
              price={vehiclePrice}
              quoteRegularPaymentInPence={quoteRegularPaymentInPence}
              quoteType={quoteType}
              discountInPence={discountInPence}
              shouldShowCarsOnOfferTag={shouldShowCarsOnOffer}
              shouldShowDepositContribution={shouldShowDepositContribution}
              vehicleId={id}
              onAdminFeesClick={onAdminFeesClick}
              quoteTermMonths={quoteTermMonths}
            />
          )}
        </div>
        <div
          data-testid="finance-search-actions"
          className={cx(styles['searchActions'], {
            [styles.financeCalcExperimentSearchActions]:
              shouldAllowFinanceCalculatorExperiment,
          })}
        >
          {/* {!customerHasActiveOrder && (
            )} */}
          <CompareCTA
            vehicleId={id}
            vehicleTitle={title}
            vehicleImage={image}
            vehicleMake={make}
            vehicleModel={model}
          />

          {!shouldAllowFinanceCalculatorExperiment ? (
            <FavouriteCTA
              isPending={isPending}
              isSaved={favourites.some((vehicle) => vehicle.vehicleId === id)}
              onClick={handleOnFavouriteClick}
            />
          ) : (
            <FinanceCalculatorCTA
              href={productDetailsLink}
              target={tabTarget}
              rel={relValue}
              vehicleId={id}
            />
          )}

          <AuthModal
            isOpen={isAuthModalOpen}
            handleClose={handleAuthModalClose}
            useBrowserNavigation={true}
            vehicleImage={image}
          />
        </div>
      </div>

      <div
        className={cx(styles['imageContainer'], imageStyles, {
          [styles['imageContainerCollapsed']]: !hasBorder,
        })}
      >
        {shouldShowImageCarousel ? (
          <VehicleImageCarousel
            images={additionalImages}
            isPending={isPending}
            isAvailable={isAvailable}
            productDetailUrl={productDetailsLink}
            ctaTitle={title}
            imageWithMorePhotosCta={
              isSearchMultipleImagesV2Variant1 ? 'Boot' : undefined
            }
            imagesToDisplay={
              isSearchMultipleImagesV2Variant2
                ? ['Front driver side', 'Interior front']
                : undefined
            }
          />
        ) : (
          <ProductImage
            alt=""
            isAvailable={isAvailable}
            image={image}
            unavailableImageMessage={
              (isSold && ' ') || unavailable || ERROR_MESSAGE
            }
            isPending={isPending}
          />
        )}

        {shouldShowCarsOnOffer &&
          !isReserved &&
          !shouldShowDepositContribution &&
          !isSold && (
            <Text
              fontWeight="semibold"
              className={cx([styles['badge'], styles['discount']])}
              data-testid="vehicle-has-discount"
              id={`vehicle-discount-${id}`}
            >
              {`£${penceToPounds(discountInPence ?? 0)} off`}
            </Text>
          )}

        {isSearchPage &&
          shouldShowDepositContribution &&
          !isReserved &&
          !isSold && (
            <Text
              fontWeight="semibold"
              className={`${styles['badge']} ${styles['deposit']}`}
              id={`vehicle-deposit-${id}`}
            >
              {`£${penceToPounds(depositContributionInPence ?? 0)}`} towards
              deposit <br />
              10.9% APR Representative
            </Text>
          )}

        {isReserved && !isSold && (
          <Text
            fontWeight="book"
            className={`${styles['badge']} ${styles['reserved']}`}
            data-testid="vehicle-is-reserved"
            id={`vehicle-reserved-${id}`}
          >
            {isSearchMultipleImagesFlagEnabled
              ? 'Reserved'
              : 'Currently reserved'}
          </Text>
        )}

        {shouldAllowFinanceCalculatorExperiment && (
          <ExperimentFavouriteCTA
            isPending={isPending}
            isSaved={favourites.some((vehicle) => vehicle.vehicleId === id)}
            onClick={handleOnFavouriteClick}
          />
        )}

        {isSold && (
          <Text
            element="span"
            fontSizeFixed="s"
            fontWeight="book"
            className={`${styles['badge']} ${styles['sold']}`}
            data-testid="vehicle-is-sold"
            id={`vehicle-sold-${id}`}
          >
            Sold
          </Text>
        )}
      </div>
    </Card>
  )
}

export default VehicleCard
